import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { UserMinus, Loader2 } from 'lucide-react';
import api from '../../services/api';

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

/**
 * Year-over-year Resignations vs Terminations comparison.
 * Shows the reference year and the immediately preceding year only.
 * Clicking a year opens a breakdown of exactly who resigned / was terminated.
 */
const ExitComparisonChart = ({ year, showCard = true }) => {
  const refYear = year || new Date().getFullYear();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillYear, setDrillYear] = useState(null);
  const [drill, setDrill] = useState({ resigned: [], terminated: [] });
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/api/hr-report/exit-comparison?year=${refYear}`)
      .then((r) => {
        if (!mounted) return;
        const comparison = r.data?.comparison || [];
        // Keep current + previous year only, in chronological order
        const wanted = comparison
          .filter((c) => c.year === refYear || c.year === refYear - 1)
          .sort((a, b) => a.year - b.year)
          .map((c) => ({
            year: String(c.year),
            Resigned: c.resigned,
            Terminated: c.terminated,
            total: c.total,
          }));
        setData(wanted);
      })
      .catch(() => { if (mounted) setData([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [refYear]);

  const openDrill = (yr) => {
    if (!yr) return;
    setDrillYear(yr);
    setDrillLoading(true);
    api.get(`/api/hr-report/exit-list?year=${yr}`)
      .then((r) => setDrill({ resigned: r.data?.resigned || [], terminated: r.data?.terminated || [] }))
      .catch(() => setDrill({ resigned: [], terminated: [] }))
      .finally(() => setDrillLoading(false));
  };

  const onBarClick = (entry) => {
    const yr = entry?.activeLabel || entry?.year || (entry?.payload && entry.payload.year);
    openDrill(parseInt(yr, 10));
  };

  const hasExits = data.some((d) => d.total > 0);

  const renderList = (items, label, color) => (
    <div className="flex-1 min-w-[220px]">
      <h4 className={`text-sm font-semibold mb-2 ${color}`}>{label} ({items.length})</h4>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">None.</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {items.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 text-sm border-b border-gray-100 pb-1.5" data-testid={`exit-emp-${e.employee_code}`}>
              <div>
                <span className="font-medium">{e.name}</span>
                <span className="text-xs text-gray-400 ml-1.5">{e.employee_code}{e.department ? ` · ${e.department}` : ''}</span>
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(e.exit_date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const chart = (
    <div className="h-56" data-testid="exit-comparison-chart">
      {loading ? (
        <div className="h-full flex items-center justify-center text-gray-400 text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading comparison…
        </div>
      ) : !hasExits ? (
        <div className="h-full flex items-center justify-center text-gray-400 text-sm">
          No resignations or terminations recorded for {refYear - 1}–{refYear}.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 16, left: 0, right: 16, bottom: 0 }} barGap={8}
            onClick={onBarClick} style={{ cursor: 'pointer' }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="year" tick={{ fontSize: 13, fontWeight: 600 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Resigned" fill="#6b7280" radius={[4, 4, 0, 0]} maxBarSize={56} cursor="pointer">
              <LabelList dataKey="Resigned" position="top" style={{ fontSize: 11, fill: '#374151' }} />
            </Bar>
            <Bar dataKey="Terminated" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={56} cursor="pointer">
              <LabelList dataKey="Terminated" position="top" style={{ fontSize: 11, fill: '#b91c1c' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  const drillDialog = (
    <Dialog open={!!drillYear} onOpenChange={(o) => { if (!o) setDrillYear(null); }}>
      <DialogContent className="max-w-2xl" data-testid="exit-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="h-5 w-5 text-rose-600" /> Exits in {drillYear}
          </DialogTitle>
        </DialogHeader>
        {drillLoading ? (
          <div className="py-10 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex flex-wrap gap-6">
            {renderList(drill.resigned, 'Resigned', 'text-gray-700')}
            {renderList(drill.terminated, 'Terminated', 'text-rose-600')}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  if (!showCard) return <>{chart}{drillDialog}</>;

  return (
    <Card data-testid="exit-comparison-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserMinus className="h-5 w-5 text-rose-600" />
          Resignations vs Terminations ({refYear - 1} vs {refYear})
        </CardTitle>
        <p className="text-xs text-gray-400">Click a year's bars to see who resigned or was terminated.</p>
      </CardHeader>
      <CardContent>{chart}</CardContent>
      {drillDialog}
    </Card>
  );
};

export default ExitComparisonChart;
